import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hydrateState, validStateValue } from '@/constants/utils/gabUi.cjs';

// Local, owner-only values. Generated defaults and shared/forked message source
// are immutable; no server write and no telemetry contains these values.
export default function useInteractiveAnswerState({ spec, userId, conversationId, messageId, canPersist, getStorage }) {
  const scope = [userId || 'guest', conversationId || 'unsaved', messageId || 'unsaved', spec.answerId, spec.schemaVersion].map(encodeURIComponent).join(':');
  const key = canPersist && userId && conversationId && messageId ? `gab-ui-state-v1:${scope}` : null;
  const definitions = JSON.stringify(spec.state);
  const initial = useMemo(() => hydrateState(spec), [definitions]);
  const [local, setLocal] = useState({ scope, values: initial, ready: !key, failed: false });
  const current = useRef({ scope, key, spec, values: initial, ready: !key, generation: 0 });
  const writes = useRef(Promise.resolve());
  // Scope changes must immediately reject stale handlers before hydration effects.
  current.current.scope = scope;
  current.current.key = key;
  current.current.spec = spec;
  useEffect(() => {
    const sameOwner = current.current.initializedScope === scope && current.current.initializedKey === key;
    const alreadyReady = sameOwner && current.current.ready;
    const values = sameOwner ? hydrateState(spec, current.current.values) : initial;
    const generation = ++current.current.generation;
    current.current = { scope, key, spec, values, ready: !key || alreadyReady, generation, initializedScope: scope, initializedKey: key };
    setLocal(previous => ({ scope, values, ready: !key || alreadyReady, failed: sameOwner && previous.failed }));
    let cancelled = false;
    if (key && !alreadyReady) (async () => {
      let values = initial, failed = false;
      try {
        const raw = await getStorage().getItem(key);
        if (raw && raw.length <= 8192) values = hydrateState(spec, JSON.parse(raw));
      } catch (_) { failed = true; }
      if (cancelled || current.current.generation !== generation || current.current.scope !== scope) return;
      current.current.values = values; current.current.ready = true;
      setLocal({ scope, values, ready: true, failed });
    })();
    return () => { cancelled = true; };
  }, [scope, key, definitions]);
  const persist = useCallback((values, remove = false, transient = false) => {
    const active = current.current;
    if (active.scope !== scope || !active.ready) return;
    active.values = values;
    setLocal(previous => ({ ...previous, scope, values, ready: true }));
    if (!active.key || transient) return;
    const target = active.key, generation = active.generation;
    writes.current = writes.current.catch(() => {}).then(async () => {
      try {
        if (remove) await getStorage().removeItem(target);
        else await getStorage().setItem(target, JSON.stringify(values));
      } catch (_) {
        if (current.current.generation === generation && current.current.scope === scope) setLocal(previous => ({ ...previous, failed: true }));
      }
    });
  }, [scope]);
  const update = useCallback((name, value, options = {}) => {
    const active = current.current, def = active.spec.state[name];
    if (active.scope !== scope || !def || !validStateValue(def, value)) return false;
    persist({ ...active.values, [name]: value }, false, options.transient === true); return true;
  }, [scope, persist]);
  const reset = useCallback(() => persist(hydrateState(current.current.spec), true), [persist]);
  return { values: local.scope === scope ? local.values : initial, ready: local.scope === scope && local.ready, failed: local.scope === scope && local.failed, update, reset };
}
